import React from 'react';
import { motion } from 'framer-motion';
import { SettingsIcon, FlameIcon, DropletsIcon, PowerIcon, WrenchIcon, ActivityIcon, ShieldCheckIcon, ShieldAlertIcon, AlertTriangleIcon, BriefcaseIcon, TrendingUpIcon, PieChartIcon, MapIcon, PresentationIcon, UsersIcon, TargetIcon } from 'lucide-react';
import { ThreeBackground } from '../components/ThreeBackground';
import { Card3D } from '../components/Card3D';
import { PageTransition } from '../components/PageTransition';
export function ExperiencePage() {
  return <PageTransition data-id="element-124">
      {/* Page Header */}
      <section className="relative bg-navy-900 pt-32 pb-20 overflow-hidden bg-glow" data-id="element-125">
        <ThreeBackground data-id="element-126" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12" data-id="element-127">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-128">
            <span className="text-gold font-bold tracking-widest text-sm uppercase mb-2 block" data-id="element-129">
              Career
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white" data-id="element-130">
              Executive Leadership Experience
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-light py-20" data-id="element-131">
        <div className="max-w-4xl mx-auto px-6 md:px-12" data-id="element-132">
          <div className="relative border-l-2 border-gold/30 pl-8 ml-4 md:ml-0 space-y-12" data-id="element-133">
            {/* Role 1 */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.5
          }} className="relative" data-id="element-134">
              <div className="absolute -left-[41px] top-1 h-4 w-4 rounded-full bg-gold border-4 border-light shadow-sm" aria-hidden="true" data-id="element-135" />
              <Card3D className="w-full" data-id="element-136">
                <div className="bg-white p-8 border border-gray-100 shadow-sm" data-id="element-137">
                  <span className="inline-block px-3 py-1 bg-light-alt text-navy-800 text-sm font-semibold tracking-wide mb-3" data-id="element-138">
                    2024 – Present
                  </span>
                  <h3 className="text-xl md:text-2xl font-bold text-ink mb-1" data-id="element-139">
                    Director, Field Operations – Gas Processing
                  </h3>
                  <h4 className="text-lg font-medium text-gold mb-4" data-id="element-140">
                    San Mateo Midstream (Matador Resources)
                  </h4>
                  <p className="text-ink-muted leading-relaxed" data-id="element-141">
                    Responsible for multi-site operations including five gas
                    processing trains totaling 720 MMSCFD capacity. Leads
                    capital projects, plant commissioning, and operational
                    strategy while advising executive leadership and board
                    stakeholders.
                  </p>
                </div>
              </Card3D>
            </motion.div>

            {/* Role 2 */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="relative" data-id="element-142">
              <div className="absolute -left-[41px] top-1 h-4 w-4 rounded-full bg-gold border-4 border-light shadow-sm" aria-hidden="true" data-id="element-143" />
              <Card3D className="w-full" data-id="element-144">
                <div className="bg-white p-8 border border-gray-100 shadow-sm" data-id="element-145">
                  <span className="inline-block px-3 py-1 bg-light-alt text-navy-800 text-sm font-semibold tracking-wide mb-3" data-id="element-146">
                    2020 – 2024
                  </span>
                  <h3 className="text-xl md:text-2xl font-bold text-ink mb-1" data-id="element-147">
                    Plant Superintendent
                  </h3>
                  <h4 className="text-lg font-medium text-gold mb-4" data-id="element-148">
                    San Mateo Midstream (Matador Resources)
                  </h4>
                  <p className="text-ink-muted leading-relaxed" data-id="element-149">
                    Managed operations for three gas processing trains, ensuring
                    compliance with Process Safety Management standards while
                    improving plant uptime and operational efficiency.
                  </p>
                </div>
              </Card3D>
            </motion.div>

            {/* Role 3 */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="relative" data-id="element-150">
              <div className="absolute -left-[41px] top-1 h-4 w-4 rounded-full bg-gold border-4 border-light shadow-sm" aria-hidden="true" data-id="element-151" />
              <Card3D className="w-full" data-id="element-152">
                <div className="bg-white p-8 border border-gray-100 shadow-sm" data-id="element-153">
                  <span className="inline-block px-3 py-1 bg-light-alt text-navy-800 text-sm font-semibold tracking-wide mb-3" data-id="element-154">
                    2017 – 2020
                  </span>
                  <h3 className="text-xl md:text-2xl font-bold text-ink mb-1" data-id="element-155">
                    Plant Operations Supervisor
                  </h3>
                  <h4 className="text-lg font-medium text-gold mb-4" data-id="element-156">
                    Targa Resources
                  </h4>
                  <p className="text-ink-muted leading-relaxed" data-id="element-157">
                    Oversaw operations of a 260 MMSCFD cryogenic gas plant while
                    supporting commercial, engineering, and executive leadership
                    teams to drive profitability and performance.
                  </p>
                </div>
              </Card3D>
            </motion.div>

            {/* Role 4 */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="relative" data-id="element-158">
              <div className="absolute -left-[41px] top-1 h-4 w-4 rounded-full bg-gold border-4 border-light shadow-sm" aria-hidden="true" data-id="element-159" />
              <Card3D className="w-full" data-id="element-160">
                <div className="bg-white p-8 border border-gray-100 shadow-sm" data-id="element-161">
                  <span className="inline-block px-3 py-1 bg-light-alt text-navy-800 text-sm font-semibold tracking-wide mb-3" data-id="element-162">
                    2015 – 2017
                  </span>
                  <h3 className="text-xl md:text-2xl font-bold text-ink mb-1" data-id="element-163">
                    Assistant Plant Manager
                  </h3>
                  <h4 className="text-lg font-medium text-gold mb-4" data-id="element-164">
                    Boardwalk Field Services (Boardwalk Pipelines)
                  </h4>
                  <p className="text-ink-muted leading-relaxed" data-id="element-165">
                    Directed plant maintenance and operational teams, improving
                    reliability and reducing downtime while managing operational
                    budgets and safety programs.
                  </p>
                </div>
              </Card3D>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Expertise */}
      <section className="bg-navy-900 py-24" data-id="element-166">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-167">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-16" data-id="element-168">
            <span className="text-gold font-bold tracking-widest text-sm uppercase mb-2 block" data-id="element-169">
              Capabilities
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white" data-id="element-170">
              Core Expertise
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12" data-id="element-171">
            {/* Operations */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-172">
              <h3 className="text-xl font-semibold text-white/90 border-b border-white/10 pb-4 mb-6" data-id="element-173">
                Operations
              </h3>
              <ul className="space-y-4" data-id="element-174">
                <li className="flex items-center" data-id="element-175">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-176">
                    <SettingsIcon size={20} data-id="element-177" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-178">
                    Gas Processing Operations
                  </span>
                </li>
                <li className="flex items-center" data-id="element-179">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-180">
                    <FlameIcon size={20} data-id="element-181" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-182">
                    Cryogenic Plant Operations
                  </span>
                </li>
                <li className="flex items-center" data-id="element-183">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-184">
                    <DropletsIcon size={20} data-id="element-185" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-186">
                    Fractionation Systems
                  </span>
                </li>
                <li className="flex items-center" data-id="element-187">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-188">
                    <PowerIcon size={20} data-id="element-189" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-190">
                    Plant Commissioning
                  </span>
                </li>
                <li className="flex items-center" data-id="element-191">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-192">
                    <ActivityIcon size={20} data-id="element-193" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-194">
                    Start-Up &amp; Shutdown
                  </span>
                </li>
                <li className="flex items-center" data-id="element-195">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-196">
                    <WrenchIcon size={20} data-id="element-197" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-198">
                    Turnarounds &amp; Maintenance
                  </span>
                </li>
                <li className="flex items-center" data-id="element-199">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-200">
                    <TrendingUpIcon size={20} data-id="element-201" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-202">
                    Process Optimization
                  </span>
                </li>
              </ul>
            </motion.div>

            {/* Safety & Compliance */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.15
          }} data-id="element-203">
              <h3 className="text-xl font-semibold text-white/90 border-b border-white/10 pb-4 mb-6" data-id="element-204">
                Safety &amp; Compliance
              </h3>
              <ul className="space-y-4" data-id="element-205">
                <li className="flex items-center" data-id="element-206">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-207">
                    <ShieldCheckIcon size={20} data-id="element-208" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-209">
                    Process Safety Management
                  </span>
                </li>
                <li className="flex items-center" data-id="element-210">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-211">
                    <ShieldAlertIcon size={20} data-id="element-212" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-213">
                    Safety Leadership
                  </span>
                </li>
                <li className="flex items-center" data-id="element-214">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-215">
                    <AlertTriangleIcon size={20} data-id="element-216" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-217">
                    Risk Management
                  </span>
                </li>
                <li className="flex items-center" data-id="element-218">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-219">
                    <ActivityIcon size={20} data-id="element-220" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-221">
                    Crisis Response
                  </span>
                </li>
              </ul>
            </motion.div>

            {/* Leadership */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-222">
              <h3 className="text-xl font-semibold text-white/90 border-b border-white/10 pb-4 mb-6" data-id="element-223">
                Leadership
              </h3>
              <ul className="space-y-4" data-id="element-224">
                <li className="flex items-center" data-id="element-225">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-226">
                    <BriefcaseIcon size={20} data-id="element-227" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-228">
                    Capital Projects
                  </span>
                </li>
                <li className="flex items-center" data-id="element-229">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-230">
                    <TargetIcon size={20} data-id="element-231" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-232">
                    Operational Efficiency
                  </span>
                </li>
                <li className="flex items-center" data-id="element-233">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-234">
                    <PieChartIcon size={20} data-id="element-235" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-236">
                    Budget &amp; Cost Control
                  </span>
                </li>
                <li className="flex items-center" data-id="element-237">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-238">
                    <MapIcon size={20} data-id="element-239" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-240">
                    Multi-Site Management
                  </span>
                </li>
                <li className="flex items-center" data-id="element-241">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-242">
                    <PresentationIcon size={20} data-id="element-243" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-244">
                    Stakeholder Reporting
                  </span>
                </li>
                <li className="flex items-center" data-id="element-245">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-246">
                    <UsersIcon size={20} data-id="element-247" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-248">
                    Team Leadership
                  </span>
                </li>
                <li className="flex items-center" data-id="element-249">
                  <div className="mr-4 p-2 rounded bg-navy-800 text-gold" data-id="element-250">
                    <TargetIcon size={20} data-id="element-251" />
                  </div>
                  <span className="text-white/80 font-medium" data-id="element-252">
                    Strategic Operations
                  </span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
    </PageTransition>;
}